import React, { useEffect, useMemo, useState } from "react";

import classNames from "classnames";
import Head from "next/head";
import { useRouter } from "next/router";

import { getFaqContent } from "data/faq";
import { getTemplateVideoData } from "data/videoProductPage";

import {
  PRODUCT_LANDING_PAGE_LOGIN,
  PRODUCT_LANDING_PAGE_VISIT,
} from "~/analytics/Amplitude/amplitudeConstants";
import { addUTMPropertiesToEvent } from "~/analytics/Amplitude/amplitudeUtils";
import { useAnalytics } from "~/analytics/useAnalytics";
import { MarketingFooter } from "~/components/common/MarketingFooter";
import { ClassVideoModal } from "~/components/videoProductPage/common/ClassVideoModal/ClassVideoModal";
import { Divider } from "~/components/videoProductPage/common/Divider/Divider";
import { AISuggestionsPanel } from "~/components/videoProductPage/components/AISuggestionsPanel/AISuggestionsPanel";
import { BehindTheScenesSection } from "~/components/videoProductPage/components/BehindTheScenes/BehindTheScenes";
import { ProductBuy } from "~/components/videoProductPage/components/ProductBuy/ProductBuy";
import { ReviewsSection } from "~/components/videoProductPage/components/Reviews/Reviews";
import { VideoSection } from "~/components/videoProductPage/components/VideoSection/VideoSection";
import { WhoIsAuthorSection } from "~/components/videoProductPage/components/WhoIsAuthor/WhoIsAuthorSection";
import { WhoIsThisVideoFor } from "~/components/videoProductPage/components/WhoIsThisVideoFor/WhoIsThisVideoFor";
import { useIsSidebarSticky } from "~/components/videoProductPage/hooks/useIsSidebarSticky";
import { useAuth } from "~/hooks/auth/useAuth";
import { CreatorEditingProvider } from "~/hooks/creatorEditing/creatorEditingProvider";
import { useCreatorEditing } from "~/hooks/useCreatorEditing";
import { usePriceOfferInfoCheckout } from "~/hooks/usePriceOfferInfo";

import { LandingNavbarWithCTA } from "../common/LandingNavbarWithCTA/LandingNavbarWithCTA";
import { AddToCartSuccess } from "../common/Modals/AddToCartSuccess";
import { OverlayModal } from "../common/OverlayModal";
import { TopBannerCTA } from "../common/TopBannerCTA/TopBannerCTA";
import { LandingPageSources } from "../homepage/useLandingPage";
import * as styles from "./Product.css";
import { Props } from "./ProductInterface";
import { RecommendedSection } from "./common/RecommendedSection/RecommendedSection";
import { FaqSection } from "./components/FaqSection";
import { ProductPageEditBar } from "./components/ProductPageEditBar/ProductPageEditBar";

const DEFAULT_OG_IMG =
  "https://res.cloudinary.com/monthly/image/upload/q_auto:best,f_auto/v1649719544/monthly-admin-images/homepage/studio-og.jpg";
const DEFAULT_OG_TITLE = "Studio: Hands-on Classes From The World's Best";

export const Product = ({
  productData,
  reviewsData,
  footerData,
  storefrontData,
  isEditingPage = false,
}: Props): React.ReactElement => {
  const { trackAmplitudeEvent } = useAnalytics();
  const [showModal, setShowModal] = useState(false);
  const [showAddToCartModal, setShowAddToCartModal] = useState(false);
  const [isInEditMode, setIsInEditMode] = useState(isEditingPage);
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [savingStatusText, setSavingStatusText] = useState("");
  const { user, loadingCurrentUser } = useAuth();
  const { fieldsTexts, isAISuggestionsShown, handleSetIsAISuggestionsShown } = useCreatorEditing();
  const { shouldUserSeeOfferCalculation, getProductPrice } = usePriceOfferInfoCheckout();
  // const { shouldUserSeeOfferCalculation, getProductPrice } = usePriceOfferInfo(
  //   productData.priceInfo,
  // );

  const [productPrice, setProductPrice] = useState(getProductPrice(productData.priceInfo, false));
  const hasVideoURL = !!productData.productPreviewURL;

  const handleShowModal = () => setShowModal(!showModal);
  const handleAddToCartShowModal = () => setShowAddToCartModal(!showAddToCartModal);

  const isSidebarSticky = useIsSidebarSticky();


  //   console.log("the reviews data is ", reviewsData);


  const faqData = getFaqContent("general-faq");
  const router = useRouter();
  const { adminCreatorSlug } = router.query;

  const sideBarClasses = useMemo(() => {
    return isInEditMode
      ? [styles.sidebarOnEditMode]
      : [styles.sidebar, isSidebarSticky ? styles.stickySidebar : styles.isNotStickySidebar];
  }, [isInEditMode, isSidebarSticky]);

  const productUrl = useMemo(() => {
    return (
      "https://studio.com/" +
      productData.storefrontSlug +
      "/" +
      productData.classSlug +
      "&ref=studio&slug=multiple&utm_source=fbig&utm_medium=paid"
    ); //router.asPath;
  }, [productData.storefrontSlug, productData.classSlug]);

  //const productPageTitle = storefrontData.creatorName.trim() + "'s Studio class. " + productData.title;
  const productPageTitle = useMemo(() => {
    return productData.title;
  }, [productData.title]);

  useEffect(() => {
    const shouldUserSeeOffer = shouldUserSeeOfferCalculation(productData.priceInfo);
    const productPrice = getProductPrice(productData.priceInfo, shouldUserSeeOffer);
    setProductPrice(productPrice);

    if (!loadingCurrentUser) {
      trackAmplitudeEvent(PRODUCT_LANDING_PAGE_VISIT, {
        ...addUTMPropertiesToEvent(),
        productId: productData.id,
        productTitle: productData.title,
        creatorName: storefrontData.creatorName,
        productPrice: productPrice,
        crossedPrice: productData.priceInfo.crossedPrice,
        showOffer: shouldUserSeeOffer,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCurrentUser, user]);

  // TODO: bring back / remove for creators to be able to edit their class
  const lockForCreatorsAllowAdmins = false;
  // if (lockForCreatorsAllowAdmins && isInEditMode && router.isReady && !adminCreatorSlug) {
  //   router.push("/classes/create/" + productData.id);
  //   return <div>Contact your Studio partner if you want to make more changes</div>;
  // }

  useEffect(() => {


  }, [isAISuggestionsShown, handleSetIsAISuggestionsShown, fieldsTexts]);

  return (
    <CreatorEditingProvider
      productData={productData}
      storefrontData={storefrontData}
      isInEditMode={isInEditMode}
    >
      <div className={styles.container}>
        <Head>
          <title>{productPageTitle}</title>
          <meta property="og:title" content={productData.title} />
          <meta
            name="og:description"
            content={storefrontData.shortBio || productData.description}
          />
          <meta
            property="og:image"
            content={productData.productImageURL ? productData.productImageURL : DEFAULT_OG_IMG}
          />
          <meta property="og:url" content={productUrl}></meta>
          <meta property="product:brand" content="Studio"></meta>
          <meta property="product:availability" content="in stock"></meta>
          <meta
            name="product:description"
            content={storefrontData.shortBio || productData.description}
          />
          <meta property="product:condition" content="new"></meta>
          <meta property="product:price:amount" content={`${productPrice / 100}`}></meta>
          <meta property="product:price:currency" content="USD"></meta>
          <meta property="product:retailer_item_id" content={productData.id}></meta>
          <meta property="product:item_group_id" content={storefrontData.id}></meta>
          <meta property="google_product_category" content={"317"}></meta>
          <link rel="canonical" href="https://studio.com/" />
        </Head>
        <div itemScope itemType="https://schema.org/Product">
          <meta itemProp="brand" content="Studio" />
          <meta itemProp="name" content={productPageTitle} />
          <meta
            itemProp="description"
            content={storefrontData.shortBio || productData.description}
          />
          <meta itemProp="productID" content={productData.id} />
          <meta itemProp="url" content={productUrl} />
          <meta
            itemProp="image"
            content={productData.productImageURL ? productData.productImageURL : DEFAULT_OG_IMG}
          />
          <div itemProp="value" itemScope itemType="https://schema.org/PropertyValue">
            <meta itemProp="propertyID" content={"item_group_id"}></meta>
            <meta itemProp="value" content={storefrontData.id}></meta>
          </div>
          <div itemProp="offers" itemScope itemType="https://schema.org/Offer">
            <link itemProp="availability" href="https://schema.org/InStock" />
            <link itemProp="itemCondition" href="https://schema.org/NewCondition" />
            <meta itemProp="price" content={`${productPrice / 100}`} />
            <meta itemProp="priceCurrency" content="USD" />
          </div>
        </div>
        <TopBannerCTA
          heroData={{
            title: "level up your skills with the world’s best online classes",
            description: "What do you want to learn?",
            backgroundUrl: "",
            mobileBackgroundUrl: "",
            titleB: "Announcing 80+ New Music Classes",
            descriptionB: "Get a special launch price through Jun 23",
            yellowBannerLinkUrl: "/browse?category=Music",
            countdownEndDate: "2023-06-24T06:59:59Z",
            descriptionAfterCountdown: "Taught by the world's best, across 30 genres & 25 topics",
            descriptionMobile: "",
            pricingText: "",
            socialLogos: [],
            titleMobile: "",
          }}
        />
        <LandingNavbarWithCTA
          hideBottomBorderInitially={true}
          onSignInEvent={() => {
            trackAmplitudeEvent(PRODUCT_LANDING_PAGE_LOGIN, addUTMPropertiesToEvent());
          }}
          source={LandingPageSources.Product}
        />
        <ClassVideoModal
          videoUrl={productData.productPreviewURL}
          isTrailerVideoOpen={showModal}
          toggleVideoModal={() => setShowModal(!showModal)}
        />

        <OverlayModal
          isOpen={showAddToCartModal}
          contentClass={styles.overlayModal}
          onClose={() => {
            setShowAddToCartModal(false);
          }}
        >
          <>
            {showAddToCartModal && (
              <AddToCartSuccess
                product={productData}
                closePopup={() => setShowAddToCartModal(false)}
              />
            )}
          </>
        </OverlayModal>

        {isInEditMode && (
          <AISuggestionsPanel
            product={productData}
            storefrontData={storefrontData}
            isOpen={isAISuggestionsShown}
          />
        )}

        {isInEditMode && <ProductPageEditBar />}
        <div className={styles.productPage}>
          <div className={styles.product}>
            <VideoSection
              product={productData}
              handleAddToCartShowModal={handleAddToCartShowModal}
              storefrontData={storefrontData}
              isInEditMode={isInEditMode}
            />
            <div className={styles.productContainer}>
              <Divider marginTop={51} marginBottom={48} />
              <WhoIsThisVideoFor productData={productData} isInEditMode={isInEditMode} />

              {reviewsData && reviewsData.length > 0 && (
                <>
                  <Divider marginTop={50} marginBottom={47} />
                  <ReviewsSection reviews={reviewsData} productData={productData} />
                </>
              )}

              <Divider marginTop={48} marginBottom={44} />
              <WhoIsAuthorSection
                // productData={productData}
                storefrontData={storefrontData}
                isInEditMode={isInEditMode}
              // setIsSavingChanges={setIsSavingChanges}
              // setSavingStatusText={setSavingStatusText}
              />
              <Divider marginTop={46} marginBottom={43} />
              <BehindTheScenesSection behindTheScenes={getTemplateVideoData().behindTheScenes} />
              {faqData && (
                <>
                  <Divider marginTop={46} marginBottom={43} />
                  <FaqSection faqData={faqData} />
                </>
              )}
            </div>
          </div>
          <div className={classNames(sideBarClasses)}>
            <div className={styles.productContainer}>
              <ProductBuy
                handleAddToCartShowModal={handleAddToCartShowModal}
                handleShowModal={handleShowModal}
                hasVideoURL={hasVideoURL}
                product={productData}
                isInEditMode={isInEditMode}
              />
            </div>
          </div>
        </div>
        <div className={styles.recommendedSectionWrap}>
          <div className={styles.productContainer}>
            <RecommendedSection
              userId={user?.userId}
              useAllPurchases={true}
              products={[productData]}
              sectionTitle="Other Studio classes you might like"
            />
          </div>
        </div>
        <MarketingFooter data={footerData} />
      </div>
    </CreatorEditingProvider>
  );
};
